import * as R from "ramda";
import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
// TODO change dropdown button
import { Icon, Checkbox, Dropdown, Divider, Collapse } from "antd";
import { useSelector } from "react-redux";

import { isPendingStep } from "../selectors";
import { getTags } from "../../../../../components/Chatbot/selectors";
import { INITIAL_CHATBOT_VALIDATION_PATH } from "../../../../../components/Chatbot/validators";
import { tagNames } from "../../../../../components/CandidateModal/Tests/SuccessProbabilityTag";
import { DEFAULT_PROCESS_STEPS } from "../../../../../constants/opportunity";

const { Panel } = Collapse;

const Filter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 50px;
  margin-right: 8px;
  padding: 5px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  color: #747c84;
  i {
    margin-right: 11px;
  }
  .arrow-down {
    width: 8px;
    height: 10px;
    border: 5px solid transparent;
    border-top-color: black;
    margin-top: 5px;
    transition: 0.15s ease all;
    transform-origin: 50% 2px;
  }
  &.active {
    background: white;
  }
`;

const FilterMenu = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  .filter-container {
    background-color: #f7f9fa;
    width: 100%;

    .ant-collapse-item {
      border-bottom: unset;
    }

    .ant-collapse-header {
      color: inherit;
    }
  }

  .tags-checkboxes {
    display: flex;
    flex-direction: column;
  }

  .ant-divider {
    margin: 8px 0;
  }
`;

const GlobalFilters = styled.div`
  width: 100%;
  padding: 18px 24px 18px 24px;

  .ant-checkbox-wrapper {
    width: 100%;
    /* padding: 6px 8px 6px 12px; */
    background-color: #fff;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &.ant-checkbox-wrapper-checked {
      font-weight: bold;
    }

    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  .ant-checkbox-inner {
    border: none;
  }

  .ant-checkbox-checked::after {
    animation: unset;
    border: unset;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: unset;
  }

  .ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: ${({ theme }) => theme["@primary"]};
  }
`;

const FilterTitle = styled.span`
  color: #000000a6;
  font-weight: bold;
`;

const FilterContent = styled.div`
  max-height: 192px;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.1);
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }
`;

const TagsFilters = styled.div`
  padding-inline: 8px;

  .ant-checkbox-wrapper {
    width: 100%;
    font-size: 12px;
    padding: 6px 8px 6px 12px;
    background-color: #fff;
    border: 1px solid #d0d6da;
    border-radius: 20px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &.ant-checkbox-wrapper-checked {
      border-color: ${({ theme }) => theme["@primary"]};
      font-weight: bold;
    }

    &.ant-checkbox-wrapper-disabled {
      border-color: #d0d6da;
    }

    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  .ant-checkbox-inner {
    border-radius: 50%;
  }

  .ant-checkbox-disabled .ant-checkbox-inner {
    background-color: unset;
  }

  .ant-checkbox-checked::after {
    animation: unset;
    border: unset;
  }
`;

export default function Filters({ step, filters, setFilters }) {
  const { t } = useTranslation(["opportunity"]);

  const [visibleFilters, setVisibleFilters] = React.useState(false);

  const onChangeCheckAll = () => {
    setFilters(
      R.pipe(
        R.assoc("type", []),
        R.assoc("status", []),
        R.assoc("successProbabilityTags", []),
        R.assoc("rooms", []),
        R.assoc("chatbotTags", []),
        R.assoc("tags", []),
        R.assoc("equifaxTags", [])
      )(filters)
    );
  };

  const onChangeFilterType = checkedValues => {
    setFilters(R.assoc("type", checkedValues, filters));
  };

  const onChangeFilterStatus = checkedValues => {
    setFilters(R.assoc("status", checkedValues, filters));
  };

  const onChangeChatbotTags = checkedValues => {
    setFilters(R.assoc("chatbotTags", checkedValues, filters));
  };

  const onChangeTags = checkedValues => {
    setFilters(R.assoc("tags", checkedValues, filters));
  };

  const onChangeSuccessProbabilityTags = checkedValues => {
    setFilters(R.assoc("successProbabilityTags", checkedValues, filters));
  };

  const onChangeRooms = checkedValues => {
    setFilters(R.assoc("rooms", checkedValues, filters));
  };

  const onChangeEquifaxTags = checkedValues => {
    setFilters(R.assoc("equifaxTags", checkedValues, filters));
  };

  const isInterviewStep = step.type === "group-interview";
  const rooms = isInterviewStep && R.map(R.prop("name"), step.rooms || []);

  const isMatchesStep = step.name === DEFAULT_PROCESS_STEPS.matches;
  const isSuccessProbabilityTestStep =
    step.type === "test" &&
    step.config.source === "worcket" &&
    step.config.idTest === "3";
  const successProbabilityTags =
    (isSuccessProbabilityTestStep || isMatchesStep) &&
    R.map(
      value => ({
        label: t(
          `opportunity:process.candidateDetails.successProbability-worcket.${value}`
        ),
        value
      }),
      R.values(tagNames)
    );

  const isEquifaxTestStep =
    step.type === "test" &&
    step.config.source === "worcket" &&
    step.config.idTest === "7";
  const equifaxTags =
    isEquifaxTestStep &&
    R.map(
      value => ({
        label: t(`opportunity:process.candidateDetails.equifaxInfo.${value}`),
        value
      }),
      ["proceed", "rejected"]
    );

  const isChatbotStep = R.propEq("type", "chatbot", step);
  const chatbotTags =
    isChatbotStep &&
    getTags(R.pathOr([], INITIAL_CHATBOT_VALIDATION_PATH, step));

  const tags = useSelector(state =>
    R.pathOr([], ["company", "tags"], state).sort()
  );

  const areFiltersActive =
    filters.type.length > 0 ||
    filters.status.length > 0 ||
    filters.successProbabilityTags.length > 0 ||
    filters.rooms.length > 0 ||
    filters.chatbotTags.length > 0 ||
    filters.tags.length > 0 ||
    filters.equifaxTags.length > 0;

  const defaultOpenFilter = R.pipe(
    R.when(
      R.propEq("isSuccessProbabilityTestStep", true),
      R.always("success-probability-tags")
    ),
    R.when(R.propEq("isEquifaxTestStep", true), R.always("equifax-tags")),
    R.when(R.propEq("isInterviewStep", true), R.always("rooms")),
    R.when(R.propEq("isChatbotStep", true), R.always("chatbot-tags"))
  )({
    isSuccessProbabilityTestStep,
    isEquifaxTestStep,
    isInterviewStep,
    isChatbotStep
  });

  const filterOptions = {
    type: [
      {
        label: t("opportunity:process.stepDetails.filters.external"),
        value: "external"
      },
      {
        label: t("opportunity:process.stepDetails.filters.internal"),
        value: "internal"
      }
    ],
    status: [
      {
        label: t("opportunity:process.stepDetails.filters.with-pending"),
        value: "with-pending",
        disabled: !isPendingStep({ step, match: {} }) // Si un candidato vacio puede pasar, significa que el step no tiene requerimientos
      },
      {
        label: t("opportunity:process.stepDetails.filters.without-pending"),
        value: "without-pending",
        disabled: !isPendingStep({ step, match: {} }) // Si un candidato vacio puede pasar, significa que el step no tiene requerimientos
      }
    ]
  };

  const filtersMenu = (
    <FilterMenu data-test="step-filters-group">
      <GlobalFilters>
        <Checkbox
          onChange={onChangeCheckAll}
          checked={
            filters.type.length === 0 &&
            filters.status.length === 0 &&
            filters.successProbabilityTags.length === 0 &&
            filters.rooms.length === 0 &&
            filters.chatbotTags.length === 0 &&
            filters.tags.length === 0
          }
          style={{ display: "block" }}
          data-test="step-filter-all"
        >
          {t("opportunity:process.stepDetails.filters.all")}
        </Checkbox>
        <Divider />
        <Checkbox.Group
          onChange={onChangeFilterType}
          className="tags-checkboxes"
          options={filterOptions.type}
          value={filters.type}
          name="type-filters"
        />
        <Divider />
        <Checkbox.Group
          onChange={onChangeFilterStatus}
          className="tags-checkboxes"
          options={filterOptions.status}
          value={filters.status}
          name="status-filters"
        />
      </GlobalFilters>
      <Collapse
        accordion
        bordered={false}
        className="filter-container"
        defaultActiveKey={defaultOpenFilter}
      >
        {tags.length > 0 && (
          <Panel
            key="tags"
            header={
              <FilterTitle data-test="step-tags-filters">
                {t("opportunity:process.stepDetails.filters.filterByTags")}
              </FilterTitle>
            }
          >
            <FilterContent>
              <TagsFilters>
                <Checkbox.Group
                  onChange={onChangeTags}
                  className="tags-checkboxes"
                  options={tags}
                  value={filters.tags}
                  name="tags-filters"
                />
              </TagsFilters>
            </FilterContent>
          </Panel>
        )}
        {(isSuccessProbabilityTestStep || isMatchesStep) && (
          <Panel
            key="success-probability-tags"
            header={
              <FilterTitle data-test="step-success-probability-tags-filters">
                {t(
                  "opportunity:process.stepDetails.filters.filterBySuccessProbabilityTag"
                )}
              </FilterTitle>
            }
          >
            <FilterContent>
              <TagsFilters>
                <Checkbox.Group
                  onChange={onChangeSuccessProbabilityTags}
                  className="tags-checkboxes"
                  options={successProbabilityTags}
                  value={filters.successProbabilityTags}
                  name="success-probability-tags-filters"
                />
              </TagsFilters>
            </FilterContent>
          </Panel>
        )}
        {isEquifaxTestStep && (
          <Panel
            key="equifax-tags"
            header={
              <FilterTitle data-test="step-equifax-tags-filters">
                {t(
                  "opportunity:process.stepDetails.filters.filterByEquifaxTag"
                )}
              </FilterTitle>
            }
          >
            <FilterContent>
              <TagsFilters>
                <Checkbox.Group
                  onChange={onChangeEquifaxTags}
                  className="tags-checkboxes"
                  options={equifaxTags}
                  value={filters.equifaxTags}
                  name="equifax-tags-filters"
                />
              </TagsFilters>
            </FilterContent>
          </Panel>
        )}
        {isInterviewStep && rooms.length > 0 && (
          <Panel
            key="rooms"
            header={
              <FilterTitle data-test="step-rooms-filters">
                {t("opportunity:process.stepDetails.filters.filterByRooms")}
              </FilterTitle>
            }
          >
            <FilterContent>
              <TagsFilters>
                <Checkbox.Group
                  onChange={onChangeRooms}
                  className="tags-checkboxes"
                  options={rooms}
                  value={filters.rooms}
                  name="rooms-filters"
                />
              </TagsFilters>
            </FilterContent>
          </Panel>
        )}
        {isChatbotStep && chatbotTags.length > 0 && (
          <Panel
            key="chatbot-tags"
            header={
              <FilterTitle data-test="step-chatbot-tags-filters">
                {t(
                  "opportunity:process.stepDetails.filters.filterByChatbotTags"
                )}
              </FilterTitle>
            }
          >
            <FilterContent>
              <TagsFilters>
                <Checkbox.Group
                  data-test="step-chatbot-tags-filters-checkboxes"
                  onChange={onChangeChatbotTags}
                  className="tags-checkboxes"
                  options={chatbotTags}
                  value={filters.chatbotTags}
                  name="chatbot-tags-filters"
                />
              </TagsFilters>
            </FilterContent>
          </Panel>
        )}
      </Collapse>
    </FilterMenu>
  );

  return (
    <div>
      <Dropdown
        visible={visibleFilters}
        onVisibleChange={flag => setVisibleFilters(flag)}
        overlay={filtersMenu}
        trigger={["click"]}
      >
        <Filter
          className={visibleFilters && "active"}
          data-test="filter-step-btn"
        >
          <Icon
            type="filter"
            theme={areFiltersActive ? "filled" : undefined}
            style={areFiltersActive ? { color: "#3d64ff" } : undefined}
          />
          <div
            className="arrow-down"
            style={visibleFilters ? { transform: "rotate(180deg)" } : {}}
          ></div>
        </Filter>
      </Dropdown>
    </div>
  );
}
